"use client";

import React, { useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import { EyeIcon, EyeOff } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function EarningsChart({
  transactions,
  title,
  subtitle,
  yOriginTitle,
  value,
}) {
  const [isVisible, setIsVisible] = React.useState(!value ? true : false);

  useEffect(() => {
    if (value) {
      if (
        window.localStorage.getItem(`${value.toLowerCase()}-hide-line-chart`)
      ) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    }
  }, []);

  // Helper function to get last 6 months
  const getLast6Months = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      months.push(dayjs().subtract(i, "month").format("MMM YYYY"));
    }
    return months;
  };

  const months = getLast6Months();

  // Convert transactions data into grouped sales data
  const groupedSales = transactions.reduce((acc, transaction) => {
    const saleMonth = dayjs.unix(transaction.created).format("MMM YYYY");
    const trAmount = value === 'invoices' ? transaction.total : transaction.amount;
    const amount = trAmount / 100; // Convert cents to dollars

    if (acc[saleMonth]) {
      acc[saleMonth] += amount;
    } else {
      acc[saleMonth] = amount;
    }
    return acc;
  }, {});

  // Map last 6 months to their respective sales data, defaulting to 0 if not present
  const salesAmounts = months.map((month) => groupedSales[month] || 0);

  // Chart data
  const data = {
    labels: months,
    datasets: [
      {
        label: subtitle || "Earnings",
        data: salesAmounts,
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.5)",
        tension: 0.3,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: title || "Earnings Overview",
        font: {
          size: 20,
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        beginAtZero: true,
        title: {
          display: true,
          text: yOriginTitle || "Earnings ($)",
        },
      },
      x: {
        type: "category",
        title: {
          display: true,
          text: "Month",
        },
      },
    },
  };

  const toggleLineChartVisibility = () => {
    if (isVisible) {
      setIsVisible(false);
      window.localStorage.setItem(
        `${value.toLowerCase()}-hide-line-chart`,
        true
      );
    } else {
      setIsVisible(true);
      window.localStorage.removeItem(`${value.toLowerCase()}-hide-line-chart`);
    }
  };

  return (
    <div
      className={`${
        isVisible ? "h-[400px] md:h-[600px]" : "h-[65px] overflow-hidden"
      }  bg-white p-4 rounded-md border relative`}
    >
      {isVisible ? (
        <Line data={data} options={options} />
      ) : (
        <h2 className="font-bold text-lg text-center text-gray-500">{title}</h2>
      )}
      {value && (
        <div className="absolute right-5 top-5">
          {isVisible ? (
            <EyeOff
              size={25}
              className="cursor-pointer"
              onClick={toggleLineChartVisibility}
            />
          ) : (
            <EyeIcon
              size={25}
              className="cursor-pointer"
              onClick={toggleLineChartVisibility}
            />
          )}
        </div>
      )}
    </div>
  );
}
